import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Tooltip from '@mui/material/Tooltip';
import Typography, { typographyClasses } from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { capitalize, get } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import AutocompleteCompanyTypes from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyTypes';
import { data as enums } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { Account } from '~hooks/useAccount/models';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import {
  AccountConfigurationDto,
  accountConfigurationSchema,
  setAccountConfigurationSchemaDefaultValues,
} from './accountConfigurationSchema';

const apiResponseErrorsMapping = {
  account_types: 'accountTypes',
} as const;

export type AccountConfigurationPanelHandler = {
  onSubmit: () => Promise<AccountConfigurationDto | undefined>;
};

interface AccountConfigurationPanelProps {
  account?: Account;
  apiResponseErrors?: ModelError_Item[];
}

const AccountConfigurationPanel = forwardRef<
  AccountConfigurationPanelHandler,
  AccountConfigurationPanelProps
>(function AccountConfigurationPanel({ account, apiResponseErrors }, ref) {
  const isEditing = Boolean(account?.id);
  const vendorJobSplittingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.vendorJobSplittingEnabled,
  });

  const {
    clearErrors,
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<AccountConfigurationDto>({
    resolver: yupResolver(accountConfigurationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setAccountConfigurationSchemaDefaultValues(account),
  });

  const accountTypes = watch('accountTypes') as string[] | undefined;
  const accountTypesError = get(errors, 'accountTypes.message', '');
  const companyType = watch('companyType') as Nullable<{ value: string }>;
  const shouldAllowUserToEditCompanyTypeDueToBackwardsCompatibility =
    isEditing && !companyType?.value;

  useEffect(() => {
    clearErrors();

    apiResponseErrors?.forEach((error) => {
      const apiField = error.field as keyof typeof apiResponseErrorsMapping;
      const formField = apiResponseErrorsMapping[apiField];

      if (formField) {
        setError(formField, { type: 'manual', message: error.message });
      }
    });
  }, [clearErrors, setError, apiResponseErrors]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, width: '100%', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <AutocompleteFormField
            control={control}
            multiple={true}
            name="accountTypes"
            errors={errors}
            list={enums.account_type.values}
            label={t('account.form.account_type')}
            isRequired={true}
            getValue={(item) => item}
            getLabel={(item) => capitalize(item)}
            inputProps={{ 'data-test-id': 'account-type' }}
          />

          {accountTypesError && (
            <FormHelperText error sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
              {accountTypesError}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <AutocompleteCompanyTypes
            control={control}
            isDisabled={
              isEditing
                ? !shouldAllowUserToEditCompanyTypeDueToBackwardsCompatibility
                : false
            }
            isRequired
            label={t('form_fields.company_type')}
            name="companyType"
          />

          <Typography variant="body2" sx={{ mt: '2px' }}>
            {t('account.form.select_owner_operator')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />
      </Box>

      {accountTypes?.includes('vendor') && (
        <>
          <Divider />

          <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <Typography variant="subtitle2">
              {t('account.form.vendor_settings')}
            </Typography>

            <Controller
              control={control}
              name="equipmentOptionsRestricted"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t('account.form.equipment_options_restricted')}
                  sx={{
                    [`& .${typographyClasses.root}`]: {
                      fontSize: '13px',
                      fontWeight: 400,
                    },
                  }}
                />
              )}
            />

            {vendorJobSplittingEnabled && (
              <Controller
                control={control}
                name="jobsSplittable"
                render={({ field }) => (
                  <Tooltip
                    title={t('account.form.allow_vendor_to_split_jobs_tooltip')}
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                        />
                      }
                      label={t('account.form.allow_vendor_to_split_jobs')}
                      sx={{
                        [`& .${typographyClasses.root}`]: {
                          fontSize: '13px',
                          fontWeight: 400,
                        },
                      }}
                    />
                  </Tooltip>
                )}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
});

export default AccountConfigurationPanel;
